import {
  Box,
  Link,
  ListItem,
  Text,
  UnorderedList,
  VStack
} from "@chakra-ui/react"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import Tip, { TipProps } from "@pathwright/web/src/modules/tips/Tip"

const MoreAboutPublishingAsDesigner = () => {
  const { tc, Trans } = useTranslate()
  return (
    <VStack gap={4} w="full" align="start">
      <Text m={0}>{tc("After you Publish this Path, you’ll be able to")}:</Text>
      <UnorderedList>
        <ListItem>
          <Trans i18nKey="invite-anyone-by-link-email-or-add-existing-members-directly">
            <strong>Invite</strong> anyone by link, email, or add existing
            members directly.
          </Trans>
        </ListItem>
        <ListItem>
          <Trans i18nKey="add-to-your-library-for-members">
            <strong>Add it to your Library</strong> for anyone or just members
            to review and self-register for.
          </Trans>
        </ListItem>
        <ListItem>
          <Trans i18nKey="optionally-set-a-price-seat-limit-access-window-and-more">
            <strong>Optionally set a price</strong>, seat limit, access window,
            and more.
          </Trans>
        </ListItem>
      </UnorderedList>
      <Text m={0}>
        <Trans i18nKey="learn-more-and-get-free-help-publishing-your-path">
          <Link href="https://help.pathwright.com/en/articles/5159775">
            Learn more
          </Link>{" "}
          and get free help publishing your Path.
        </Trans>
      </Text>
    </VStack>
  )
}

const MoreAboutPublishingAsTeacher = () => {
  const { tc, Trans } = useTranslate()
  return (
    <VStack gap={4} w="full" align="start">
      <Text m={0}>{tc("After you Publish this Path, you’ll be able to")}:</Text>
      <UnorderedList>
        <ListItem>
          <Trans i18nKey="invite-anyone-by-link-email-or-add-existing-members-directly">
            <strong>Invite</strong> anyone by link, email, or add existing
            members directly.
          </Trans>
        </ListItem>
      </UnorderedList>
      <Text m={0}>
        <Trans i18nKey="learn-more-and-get-free-help-publishing-your-path">
          <Link href="https://help.pathwright.com/en/articles/5159775">
            Learn more
          </Link>{" "}
          and get free help publishing your Path.
        </Trans>
      </Text>
    </VStack>
  )
}

const MoreAboutSyncingAsDesigner = () => {
  const { Trans } = useTranslate()
  return (
    <VStack gap={4} w="full" align="start">
      <Text m={0}>
        <Trans i18nKey="use-source-path-to-keep-cohorts-synced">
          Sync the latest, best version of your Path to your Source Path to
          quickly publish them to all Cohorts.{" "}
          <Link href="https://help.pathwright.com/en/articles/5159775-sync-how-to-keep-your-content-in-sync-across-multiple-cohorts">
            Learn more about Source Paths & Cohorts
          </Link>
          .
        </Trans>
      </Text>
    </VStack>
  )
}

const MoreAboutOffering = () => {
  const { Trans } = useTranslate()
  return (
    <VStack gap={4} w="full" align="start">
      <Text m={0}>
        <Trans i18nKey="your-path-is-ready-to-share">
          Your Path is now ready to share with others to take! See a few
          suggested next steps below or{" "}
          <Link href="https://help.pathwright.com/en/articles/5159775">
            get tips for publishing your Paths
          </Link>
          .
        </Trans>
      </Text>
    </VStack>
  )
}

const MoreAboutSourcePaths = () => {
  const { Trans } = useTranslate()
  return (
    <VStack gap={4} w="full" align="start">
      <Text m={0}>
        <Trans i18nKey="synced-changes-with-source-path">
          Now that you’ve synced changes with your Source Path, you can Publish
          them from the Source Path to all Cohorts.{" "}
          <Link href="https://help.pathwright.com/en/articles/5159775-sync-how-to-keep-your-content-in-sync-across-multiple-cohorts">
            Learn more about Source Paths
          </Link>
          .
        </Trans>
      </Text>
    </VStack>
  )
}

type SyncCardTipMapType = Record<
  string,
  {
    title: string
    content: () => JSX.Element
  } & Pick<TipProps, "icon" | "colorScheme">
>

const syncCardTipMap = {
  "more-about-publishing-as-designer": {
    title: "More about Publishing...",
    content: MoreAboutPublishingAsDesigner,
    colorScheme: "light",
    icon: "lightbulb-on"
  },
  "more-about-publishing-as-teacher": {
    title: "More about Publishing...",
    content: MoreAboutPublishingAsTeacher,
    colorScheme: "light",
    icon: "lightbulb-on"
  },
  "more-about-syncing-as-designer": {
    title: "More about syncing...",
    content: MoreAboutSyncingAsDesigner,
    colorScheme: "light",
    icon: "lightbulb-on"
  },
  "path-publish-success": {
    title: "Published!",
    content: MoreAboutOffering,
    colorScheme: "dark",
    icon: "lightbulb-on"
  },
  "path-sync-success": {
    title: "Source Path Synced",
    content: MoreAboutSourcePaths,
    colorScheme: "dark",
    icon: "lightbulb-on"
  }
} as const satisfies SyncCardTipMapType

export type SyncCardTipId = keyof typeof syncCardTipMap

type PathPublishTipProps = {
  tipId: SyncCardTipId | null
} & Partial<TipProps>

const CardTip = ({ tipId, ...tipProps }: PathPublishTipProps) => {
  if (!tipId) return null

  const { tc } = useTranslate()

  const { title: ogTitle, content, colorScheme, icon } = syncCardTipMap[tipId]

  const title = tc(ogTitle)

  return (
    <Box
      w="full"
      zIndex={111}
      sx={{
        li: {
          textAlign: "left"
        },
        ".chakra-accordion__button": {
          paddingBottom: 0
        }
      }}
    >
      <Tip
        tip={{
          id: tipId,
          title,
          content
        }}
        layout="accordion-pill"
        defaultState={"expanded"}
        colorScheme={colorScheme}
        icon={icon}
        {...tipProps}
      />
    </Box>
  )
}

export default CardTip
